import React from "react";
import moment from "moment";
import { useLocation } from "react-router";
import { push } from "connected-react-router";
import { Box, Button, Divider, Stack } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import { Beds } from "../Bed/Beds";
import { RootState } from "../../../store";
import { BedSearch } from "../Bed/BedSearch";
import Header from "../../DashboardV2/Header";
import { BedKpiKey } from "../../../constants/bed";
import BedsKpi, { getBedKpis } from "../Bed/BedsKpi";
import { Room } from "../../../interfaces/RoomInterface";
import { useIpdStats } from "../../../hooks/query/useIpds";
import { TBedKpiKey } from "../../../interfaces/BedInterface";
import { TWardType, Ward } from "../../../interfaces/WardInterface";
import { IPD, TriagePriority } from "../../../interfaces/IpdInterface";
import { ExtendedBed, useWardTypeData } from "../../../hooks/useWardTypeData";
import { IpdAdmissionCreateEdit } from "../IpdAdmission/IpdAdmissionCreateEdit";
import { IpdSettingsPanel } from "../../ResourceCentre/ModuleSettings/IpdSettings/IpdSettingsPanel";

export interface Filter {
  selectedWard: Ward | null;
  selectedRoom: Room | null;
  bedNumber: string | null;
  kpiKey: string;
}

type BedsFilterBasis =
  | { type: "all" }
  | {
      type: "kpi";
      key: TBedKpiKey;
    }
  | { type: "triage"; priority: TriagePriority };

// eslint-disable-next-line import/prefer-default-export
export function WardBeds({ wardType }: { wardType: TWardType }): React.ReactNode {
  const dispatch = useDispatch();
  const location = useLocation();

  // custom hooks
  const { extendedBeds, rooms, wards } = useWardTypeData({ wardType });

  // selectors
  const triagePriorityMap = useSelector(
    (state: RootState) => state.bedManagement.triagePriorityMap
  );

  // local states
  const [selectedDate, setSelectedDate] = React.useState(moment());
  const [openCreateIpdPanel, setOpenCreateIpdPanel] = React.useState(false);
  const [filterBasis, setFilterBasis] = React.useState<BedsFilterBasis>({
    type: "all"
  });

  const [filter, setFilter] = React.useState<Filter>({
    selectedWard: null,
    selectedRoom: null,
    bedNumber: "",
    kpiKey: ""
  });

  // memos
  const roomFilteredBeds: ExtendedBed[] = React.useMemo(() => {
    let wardBeds = extendedBeds.filter((b) => rooms.some((r) => Number(r.id) === Number(b.roomId)));

    if (filter.selectedWard?.id) {
      const wardRoomIds = rooms
        .filter((r) => Number(r.wardId) === Number(filter.selectedWard?.id))
        .map((r) => Number(r.id));
      wardBeds = wardBeds.filter((bed) => wardRoomIds.includes(Number(bed.roomId)));
    }

    if (filter.selectedRoom?.id) {
      wardBeds = wardBeds.filter((bed) => Number(bed.roomId) === Number(filter.selectedRoom?.id));
    }

    if (String(filter.bedNumber || "").trim()) {
      wardBeds = wardBeds.filter((bed) =>
        bed.bedSlug.includes(String(filter.bedNumber).toLowerCase())
      );
    }

    return wardBeds;
  }, [rooms, extendedBeds, filter.selectedWard?.id, filter.selectedRoom?.id, filter.bedNumber]);

  const kpiFilteredBeds: ExtendedBed[] = React.useMemo(() => {
    let wardBeds = [...roomFilteredBeds];

    if (filterBasis.type === "kpi") {
      switch (filterBasis.key) {
        case BedKpiKey.TOTAL_BEDS:
          // eslint-disable-next-line no-self-assign
          wardBeds = wardBeds;
          break;

        case BedKpiKey.AVAILABLE_BEDS:
          wardBeds = wardBeds.filter((bed) => !bed.isOccupied);
          break;

        case BedKpiKey.OCCUPIED_BEDS:
          wardBeds = wardBeds.filter((bed) => bed.isOccupied);
          break;

        case BedKpiKey.ADMITTED_TODAY:
          wardBeds = wardBeds.filter(
            (bed) =>
              bed.isOccupied &&
              bed.activeIpd?.dateOfAdmission &&
              moment(bed.activeIpd.dateOfAdmission).isSame(selectedDate, "day")
          );
          break;

        case BedKpiKey.DISCHARGED_TODAY:
          wardBeds = [];
          break;

        default:
          break;
      }
    }

    return wardBeds;
  }, [roomFilteredBeds, filterBasis, selectedDate]);

  // data hooks
  const { data: ipdStats } = useIpdStats({
    from: moment(selectedDate).startOf("day").toISOString(),
    until: moment(selectedDate).endOf("day").toISOString()
  });

  // kpis
  const totalBeds = roomFilteredBeds.length;
  const occupiedBeds = roomFilteredBeds.filter((bed) => bed.isOccupied).length;
  const availableBeds = totalBeds - occupiedBeds;

  const kpis = getBedKpis({
    totalBeds,
    occupiedBeds,
    availableBeds,
    admittedToday: ipdStats?.data?.admitted || 0,
    dischargedToday: ipdStats?.data?.discharged || 0
  });

  return (
    <>
      <Box>
        {/* header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", lg: "center" },
            flexDirection: { xs: "column", lg: "row" },
            gap: 2
          }}
        >
          <Header
            selectedDate={selectedDate}
            onDateChange={(date) => setSelectedDate(date)}
            showButtonGroup={false}
            showAd={false}
          />

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems="center"
            width={{ xs: "100%", lg: "auto" }}
            sx={{
              flexShrink: 0
            }}
          >
            <BedSearch
              wards={wards}
              rooms={rooms}
              filter={filter}
              onBedNumberChange={(value: string) =>
                setFilter((prev) => ({
                  ...prev,
                  bedNumber: value
                }))
              }
              onWardChange={(ward: Ward | null) => {
                setFilter((prev) => ({ ...prev, selectedWard: ward, selectedRoom: null }));
              }}
              onRoomChange={(room: Room | null) => {
                setFilter((prev) => ({ ...prev, selectedRoom: room }));
              }}
            />

            <Button variant="contained" onClick={() => setOpenCreateIpdPanel(true)}>
              Admit Patient
            </Button>

            <IpdSettingsPanel />
          </Stack>
        </Box>

        <Divider />

        {/* kpi */}
        <BedsKpi
          kpis={kpis}
          activeKpiKey={(() => {
            if (filterBasis.type === "all") {
              return BedKpiKey.TOTAL_BEDS;
            }

            if (filterBasis.type === "kpi") {
              return filterBasis.key;
            }

            return null;
          })()}
          onKpiClick={(key) =>
            setFilterBasis(
              key === BedKpiKey.TOTAL_BEDS
                ? { type: "all" }
                : { type: "kpi", key: key as TBedKpiKey }
            )
          }
        />

        <Divider />

        {/* beds */}
        <Beds
          filteredBeds={kpiFilteredBeds}
          triagePriorityMap={triagePriorityMap}
          wardType={wardType}
          rooms={rooms}
        />
      </Box>

      {/* panels */}
      {openCreateIpdPanel && (
        <IpdAdmissionCreateEdit
          defaultIpdCase={wardType}
          onClose={() => setOpenCreateIpdPanel(false)}
          onCreateSuccess={(createdIpd: IPD) => {
            setOpenCreateIpdPanel(false);
            dispatch(push(`${location.pathname}?tab=patients&ipdId=${createdIpd.id}`));
          }}
        />
      )}
    </>
  );
}
